import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { 
  MessageSquare, 
  Printer, 
  Copy, 
  Sparkles,
  Hash,
  Flame,
  Zap,
  Loader2,
  ArrowRight
} from "lucide-react";

export default function FlashMeeting() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  
  const today = new Date().toISOString().split('T')[0];
  
  const [formData, setFormData] = useState({
    date: today,
    the_numbers: "",
    the_hot_spot: "",
    executive_action: "",
    notes: "",
  });

  const [aiDraft, setAiDraft] = useState({
    context: "",
    problem: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, meetingsRes] = await Promise.all([
        axios.get(`${API}/incidents/summary?days=1`, { withCredentials: true }),
        axios.get(`${API}/flash-meetings`, { withCredentials: true }),
      ]);
      
      setSummary(summaryRes.data);
      setMeetings(meetingsRes.data || []);

      // Pre-fill numbers from summary
      if (summaryRes.data) {
        const types = Object.entries(summaryRes.data.by_type || {})
          .map(([type, count]) => `${type}: ${count}`)
          .join(", ");
        
        setFormData(prev => ({
          ...prev,
          the_numbers: `Total: ${summaryRes.data.total_incidents}. ${types || "No incidents"}`,
          the_hot_spot: summaryRes.data.hot_spot?.unit 
            ? `${summaryRes.data.hot_spot.unit} (${summaryRes.data.hot_spot.count} incidents)`
            : "No hot spots identified",
        }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveMeeting = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/flash-meetings`, formData, { withCredentials: true });
      toast.success("Flash Meeting prep saved!");
      fetchData();
    } catch (error) {
      toast.error("Failed to save");
    }
  };

  const generateAIDraft = async () => {
    if (!aiDraft.context || !aiDraft.problem) {
      toast.error("Please provide context and problem");
      return;
    }

    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/draft`, aiDraft, { withCredentials: true });
      toast.success("AI draft generated!");
      setFormData(prev => ({
        ...prev,
        notes: prev.notes + (prev.notes ? "\n\n" : "") + "--- AI Draft ---\n" + response.data.draft,
      }));
    } catch (error) {
      console.error("AI error:", error);
      toast.error("Failed to generate AI draft");
    } finally {
      setAiLoading(false);
    }
  };

  const copyToClipboard = () => {
    const text = `FLASH MEETING PREP - ${formData.date}

📊 THE NUMBERS:
${formData.the_numbers}

🔥 THE HOT SPOT:
${formData.the_hot_spot}

⚡ EXECUTIVE ACTION:
${formData.executive_action}

${formData.notes ? `NOTES:\n${formData.notes}` : ""}`;

    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const printPrep = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="flash-meeting-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-violet-500" />
            Flash Meeting Prep
          </h1>
          <p className="text-muted-foreground">Daily 09:30 meeting preparation</p>
        </div>
        
        <div className="flex gap-2 no-print">
          <Button variant="outline" onClick={copyToClipboard} data-testid="copy-prep">
            <Copy className="w-4 h-4 mr-2" />
            Copy
          </Button>
          <Button variant="outline" onClick={printPrep} data-testid="print-prep">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* 3-Bullet Summary Card */}
          <Card className="bento-card print:shadow-none print:border-gray-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Executive Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={saveMeeting} className="space-y-4">
                {/* The Numbers */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-blue-500/20 flex items-center justify-center">
                      <Hash className="w-4 h-4 text-blue-400" />
                    </div>
                    1. The Numbers
                  </Label>
                  <Textarea
                    value={formData.the_numbers}
                    onChange={(e) => setFormData({ ...formData, the_numbers: e.target.value })}
                    placeholder="Incident counts by type..."
                    rows={2}
                    data-testid="the-numbers"
                  />
                </div>

                {/* The Hot Spot */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-orange-500/20 flex items-center justify-center">
                      <Flame className="w-4 h-4 text-orange-400" />
                    </div>
                    2. The Hot Spot
                  </Label>
                  <Textarea
                    value={formData.the_hot_spot}
                    onChange={(e) => setFormData({ ...formData, the_hot_spot: e.target.value })}
                    placeholder="Pattern/unit/time analysis..."
                    rows={2}
                    data-testid="the-hot-spot"
                  />
                </div>

                {/* Executive Action */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-emerald-500/20 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-emerald-400" />
                    </div>
                    3. Executive Action
                  </Label>
                  <Textarea
                    value={formData.executive_action}
                    onChange={(e) => setFormData({ ...formData, executive_action: e.target.value })}
                    placeholder="Immediate next step..."
                    rows={2}
                    data-testid="executive-action"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Other talking points, follow-ups..."
                    rows={4}
                    data-testid="meeting-notes"
                  />
                </div>

                <Button type="submit" className="w-full" data-testid="save-meeting">
                  Save Flash Meeting Prep
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6 no-print">
          {/* Incident Summary */}
          <Card className="bento-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground">24h Incident Data</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">Total Incidents</span>
                <Badge variant="outline" className="font-mono">
                  {summary?.total_incidents || 0}
                </Badge>
              </div>
              
              {summary?.by_type && Object.entries(summary.by_type).length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">By Type:</p>
                  {Object.entries(summary.by_type).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between text-xs">
                      <span>{type}</span>
                      <span className="text-muted-foreground">{count}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {summary?.hot_spot && (
                <div className="pt-2 border-t border-zinc-800">
                  <p className="text-xs text-muted-foreground">Hot Spot:</p>
                  <p className="text-sm font-medium text-orange-400">
                    {summary.hot_spot.unit} ({summary.hot_spot.count})
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* AI Drafting Tool */}
          <Card className="bento-card border-violet-500/30">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-400" />
                AI Problem Solver
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs">Context</Label>
                <Input
                  value={aiDraft.context}
                  onChange={(e) => setAiDraft({ ...aiDraft, context: e.target.value })}
                  placeholder="e.g., Unit B staffing concerns"
                  className="text-sm"
                  data-testid="ai-context"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-xs">Problem to Solve</Label>
                <Textarea
                  value={aiDraft.problem}
                  onChange={(e) => setAiDraft({ ...aiDraft, problem: e.target.value })}
                  placeholder="What do you need help working through?"
                  rows={3}
                  className="text-sm"
                  data-testid="ai-problem"
                />
              </div>
              
              <Button 
                onClick={generateAIDraft} 
                disabled={aiLoading}
                className="w-full capture-btn"
                data-testid="generate-ai-draft"
              >
                {aiLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Thinking...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Draft
                  </>
                )}
              </Button>
              
              <p className="text-xs text-muted-foreground text-center">
                Quick draft adds to your notes
              </p>
              
              <div className="pt-3 border-t border-zinc-800">
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate("/ai-assistant")}
                  data-testid="go-to-ai-assistant"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Full AI Discussion
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  For deeper problem-solving
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Previous Meetings */}
          {meetings.length > 0 && (
            <Card className="bento-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-muted-foreground">Recent Meetings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {meetings.slice(0, 5).map((meeting) => (
                  <div 
                    key={meeting.meeting_id}
                    className="text-xs p-2 rounded bg-zinc-800/50 cursor-pointer hover:bg-zinc-800"
                    onClick={() => setFormData({
                      date: meeting.date,
                      the_numbers: meeting.the_numbers,
                      the_hot_spot: meeting.the_hot_spot,
                      executive_action: meeting.executive_action,
                      notes: meeting.notes || "",
                    })}
                  >
                    <p className="font-medium">{meeting.date}</p>
                    <p className="text-muted-foreground truncate">{meeting.executive_action}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
